import { useMutation } from "@apollo/client/react";
import {
  DSDropdown,
  DSDropdownOption,
} from "@components/design-system/molecules";
import EDIT_USER from "@graphql/mutations/EditUser";
import { useCurrentUser } from "@hooks/use-current-user";
import {
  usePimsExportFormat,
  useSetPimsExportFormat,
  PIMS_EXPORT_FORMAT_DEFAULT,
} from "@hooks/use-pims-export-format";
import mixpanel from "mixpanel-browser";

import { alert } from "../common/Alert";

type PimsType = "cloud" | "desktop" | "unknown";
type TextBehavior = "rich_text" | "plain_text" | "markdown";

type PimsProviderOption = DSDropdownOption & {
  value: string;
  pimsType: PimsType;
  textBehavior: TextBehavior;
};

const PIMS_PROVIDER_OPTIONS: PimsProviderOption[] = [
  {
    label: "ezyVet",
    value: "ezyvet",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Cornerstone",
    value: "cornerstone",
    pimsType: "desktop",
    textBehavior: "rich_text",
  },
  {
    label: "AVImark",
    value: "avimark",
    pimsType: "desktop",
    textBehavior: "plain_text",
  },
  {
    label: "Impromed",
    value: "impromed",
    pimsType: "desktop",
    textBehavior: "plain_text",
  },
  {
    label: "Shepherd",
    value: "shepherd",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Vetspire",
    value: "vetspire",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Neo",
    value: "neo",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Pulse / eVetPractice",
    value: "pulse",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Digitail",
    value: "digitail",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Instinct",
    value: "instinct",
    pimsType: "desktop",
    textBehavior: "plain_text",
  },
  {
    label: "Provet Cloud",
    value: "provet",
    pimsType: "cloud",
    textBehavior: "rich_text",
  },
  {
    label: "Other",
    value: "other",
    pimsType: "unknown",
    textBehavior: "markdown",
  },
  {
    label: "None / Not sure",
    value: "none",
    pimsType: "unknown",
    textBehavior: "markdown",
  },
];

export { PIMS_PROVIDER_OPTIONS };

export default function PimsProviderSetting() {
  const { user, refetch } = useCurrentUser();
  const [editUser, { loading: editLoading }] = useMutation(EDIT_USER);
  const currentExportFormat = usePimsExportFormat();
  const { setFormat } = useSetPimsExportFormat();

  const userAny = user as Record<string, unknown> | undefined;
  const currentProvider = (userAny?.pimsProvider as string) || "";
  const loading = editLoading;

  const handleChange = async (value: string) => {
    try {
      await editUser({
        variables: { pimsProvider: value } as Record<string, unknown>,
      });

      // Auto-set export format to the recommended one for this PIMS,
      // but only if the user is still on the default (hasn't customized).
      const option = PIMS_PROVIDER_OPTIONS.find(
        (o) => o.value === value,
      );
      if (
        option &&
        currentExportFormat === PIMS_EXPORT_FORMAT_DEFAULT
      ) {
        const recommended = option.textBehavior;
        if (recommended !== currentExportFormat) {
          await setFormat(recommended);
        }
      }

      await refetch();
      alert("success", "PIMS provider updated successfully.");
      mixpanel.track("Updated Account Setting", {
        settingName: "PIMS Provider",
        settingKey: "user.pimsProvider",
        settingValue: value,
      });
    } catch {
      alert(
        "error",
        "Failed to save PIMS provider. Please try again.",
      );
    }
  };

  return (
    <div className="my-sm">
      <DSDropdown<PimsProviderOption>
        options={PIMS_PROVIDER_OPTIONS}
        value={currentProvider}
        onChange={(_, option) => handleChange(option.value)}
        disabled={loading}
        placeholder="Select your PIMS"
        dataTestId="pims-provider-dropdown"
      />
    </div>
  );
}
